import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { radius } from '@karehero/llama/theme/radius';

import { useInspect } from '@/designs-dev/inspect/InspectContext';
import LayersTree from '@/designs-dev/inspect/LayersTree';
import { CHROME_VH, CHROME_ZOOM } from '@/designs-dev/inspect/chrome';

// While drilling, the layers tree docks on the far left so the panel reads
// Figma-Dev-Mode style (layers left, details right).
//
// Docked rather than floating: it used to be fixed over the left of the page,
// which was fine on a wide monitor and covered the phone on a laptop, where the
// stage is narrow enough to sit under it. Now it takes its own column, put first
// with `order` because every stage mounts it after the screen. Narrower on
// smaller screens, and gone on a phone, where there is no room for a tree.
const MENU_WIDTH = { md: 260, lg: 300, xl: 340 };

// A keyboard-shortcut hint: a keycap + what it does. Grouped in the panel
// footer so drill-mode shortcuts are discoverable in one place.
const ShortcutHint = ({
  keyLabel,
  label,
}: {
  keyLabel: string;
  label: string;
}) => (
  <Stack
    sx={{ alignItems: 'center', color: 'text.disabled' }}
    direction='row'
    spacing={0.5}
  >
    <Box
      sx={{
        px: 0.5,
        border: 1,
        borderColor: 'custom.neutral.200',
        borderRadius: `${radius.sm}px`,
        fontFamily: 'monospace',
        fontSize: 11,
        lineHeight: 1.5,
      }}
      component='span'
    >
      {keyLabel}
    </Box>
    <Typography variant='caption' color='text.disabled'>
      {label}
    </Typography>
  </Stack>
);

const LayersPanel = () => {
  const { active, showLayout, setShowLayout } = useInspect();
  // Show is the default: the tree includes the layout primitives (Stack/Box) that
  // hold a screen together, and tweaking is only offered there. Hide strips it
  // back to the named components for reading the composition on its own.
  // The state lives in InspectContext rather than here, because the tweak panel
  // reads it too.
  if (!active) return null;

  return (
    <Box
      sx={{
        order: -1,
        position: 'sticky',
        top: 0,
        zoom: CHROME_ZOOM,
        height: CHROME_VH(100),
        width: MENU_WIDTH,
        flexShrink: 0,
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'custom.neutral.200',
        p: 2,
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
      }}
    >
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5,
          flexShrink: 0,
        }}
        direction='row'
      >
        <Typography variant='overline' color='text.secondary'>
          Layers
        </Typography>
        <ToggleButtonGroup
          exclusive
          size='small'
          value={showLayout ? 'show' : 'hide'}
          onChange={(_, v) => v != null && setShowLayout(v === 'show')}
        >
          <ToggleButton value='show'>Show</ToggleButton>
          <ToggleButton value='hide'>Hide</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <LayersTree showLayout={showLayout} />
      </Box>
      {/* Drill-mode keyboard shortcuts, grouped so they're discoverable. */}
      <Stack sx={{ mt: 1, pt: 1, flexShrink: 0 }} direction='row' spacing={2}>
        <ShortcutHint keyLabel='\' label='toggle all' />
        <ShortcutHint keyLabel='Esc' label='exit drill' />
      </Stack>
    </Box>
  );
};

export default LayersPanel;
